import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { KEY_CODE_RETURN } from '../../global';
import { login } from '../../state/auth/auth.actions';
import { selectIsLoggedIn, selectLoginPageError } from '../../state/auth/auth.selector';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { PasswordInputComponent } from '../../password-input/password-input.component';

@Component({
  selector: 'app-login-card',
  templateUrl: './login-card.component.html',
  styleUrls: ['./login-card.component.scss'],
  standalone: true,
  imports: [
    PasswordInputComponent,
    CardModule,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    ButtonModule,
  ],
})
export class LoginCardComponent implements OnInit, OnDestroy {
  private readonly onDestroy = new Subject<void>();

  /**
   * The path to the ForgotPassword page.
   */
  @Input() forgotPasswordPath?: string;

  /**
   * FormControl for email input.
   */
  email = new UntypedFormControl('', [Validators.required, Validators.email]);

  /**
   * FormControl for password input.
   */
  password = new UntypedFormControl('', [Validators.required]);

  /**
   * Flag to indicate whether to display error message on screen.
   */
  invalidLogin = false;

  /**
   * Flag to indicate whether the store is attempting to log the user in.
   */
  isLoggingIn = false;
  isLoggedIn: Observable<boolean | null>;
  /**
   * Subscribe to changes on each FormControl to clear error messages
   * when typing after a failed login attempt.
   */
  constructor(private store: Store, private router: Router) {
    this.isLoggedIn = store.select(selectIsLoggedIn);
    combineLatest([this.email.valueChanges, this.password.valueChanges])
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.invalidLogin = false;
      });

    this.store
      .select(selectLoginPageError)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((errorMsg) => {
        if (errorMsg) {
          this.invalidLogin = true;
        }
      });
  }

  ngOnInit(): void { }

  /**
   * Unsubscribes from both FormControl valueChanges.
   */
  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  /**
   * Calls the login function when the "Enter/Return" key is pressed.
   *
   * @param event Object passed by (keyup) property of input tag.
   */
  handleKeyUp(event: any) {
    if (event.keyCode == KEY_CODE_RETURN) {
      this.login();
    }
  }

  /**
   * Attempt to log in using the provided email and password.
   * If the attempt succeeds, navigate to the patient-list page.
   * If the attempt fails, set the invalidLogin flag to true to show
   * an error message.
   */
  login() {
    this.isLoggingIn = true;
    this.store.dispatch(login({ username: this.email.value, password: this.password.value }));
    this.isLoggingIn = false;
  }
}
